import { api, getCsrfToken } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchTranslation, login } from "store/userSlice";
import * as Sentry from "@sentry/react";
import { clearCookie, isUserCookie } from "helpers/cookie";
import { apiBaseUrl } from "helpers/runtimeConfig";
import { ReactComponent as Logo } from "assets/images/peb-logo.svg";
import ErrorAlert from "components/ui/ErrorAlert";
import { doLogout } from "helpers/logout";
import { isLicenseBlockedTranslationResult, runPostLogin } from "helpers/postLogin";
import { Button, Field, Input, InputAffix } from 'components/ui';

export default function Login() {
  // --- URL params ---
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get('code');
  const iss = searchParams.get('iss');
  const { accountId } = useParams();

  // --- State ---
  const dispatch = useDispatch();
  const { register, handleSubmit, setValue, getValues } = useForm({
    defaultValues: { account_id: accountId || localStorage.getItem('accountId') || '' },
  });

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingKeycloak, setLoadingKeycloak] = useState(!!code);
  const [accountIdReadOnly, setAccountIdReadOnly] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // --- Helpers ---

  const onAccountIdChange = (e) => {
    if (e.target.value) {
      localStorage.setItem('accountId', e.target.value);
    } else {
      clearAccountId();
    }
  };

  const clearAccountId = () => {
    setValue('account_id', '');
    localStorage.removeItem('accountId');
    localStorage.removeItem('period');
    localStorage.removeItem('module');
    localStorage.removeItem('mode_scope');
    setAccountIdReadOnly(false);
    clearCookie();
  };

  // --- Auth functions ---

  const keycloakLogin = async () => {
    try {
      setError(null);
      setLoadingKeycloak(true);

      await getCsrfToken();
      const { data, status } = await api.post('/keycloak/login', { code, iss }, { baseURL: apiBaseUrl });

      if (status === 200) {
        if (data.data?.account_id) {
          localStorage.setItem('accountId', data.data.account_id);
        }

        const { blocked } = await runPostLogin(dispatch, data.data);
        if (blocked) {
          setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            next.delete('code');
            next.delete('session_state');
            next.delete('iss');
            return next;
          }, { replace: true });
          setLoadingKeycloak(false);
        }
      }
    } catch (err) {
      if (err.response?.status === 403) {
        localStorage.removeItem('period');
      }
      Sentry.captureException(err);
      setError(errorMessage(err));
      setLoadingKeycloak(false);
    }
  };

  const keycloakGetLogin = async () => {
    if (!localStorage.getItem('accountId')) {
      setError({ message: 'Account-ID is required.', translationKey: 'message.error.account_id_required' });
      return;
    }

    localStorage.removeItem('period');
    localStorage.removeItem('module');
    localStorage.removeItem('mode_scope');

    setError(null);
    setLoadingKeycloak(true);

    // Set flag before redirect so any return to /login (cancel, back-button) won't auto-redirect
    sessionStorage.setItem('skip_autologin', '1');

    try {
      api.defaults.headers['X-Tenant'] = localStorage.getItem('accountId');
      const { data } = await api.get('/keycloak/login', { baseURL: apiBaseUrl });
      window.location.href = data.uri;
    } catch (err) {
      Sentry.captureException(err);
      setError(errorMessage(err));
      setLoadingKeycloak(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setError(null);
      setLoading(true);

      localStorage.removeItem('period');
      localStorage.removeItem('module');
      localStorage.removeItem('mode_scope');

      await getCsrfToken();
      await dispatch(login({ ...data })).unwrap();
      const translationResult = await dispatch(fetchTranslation(true));
      if (isLicenseBlockedTranslationResult(translationResult)) {
        setLoading(false);
        return;
      }

      window.location.href = '/employee-management?with=changelogs';
    } catch (err) {
      if (err.response?.status === 403) {
        localStorage.removeItem('period');
      }
      setError(err);
      setLoading(false);
    }
  };

  // --- Effects ---

  useEffect(() => {
    // 1. Initialize account ID from URL param or localStorage
    if (accountId) {
      // If there is an active session, logout first
      if (isUserCookie()) {
        doLogout(dispatch, 'Automatic logout because custom login link is used.', true, `/login/${accountId}`);
      }
      localStorage.removeItem('period');
    }

    localStorage.setItem('accountId', getValues('account_id'));

    if (accountId || localStorage.getItem('accountId')) {
      setAccountIdReadOnly(true);
    }

    // 2. Handle Keycloak auth code callback (?code=...)
    if (code) {
      if (!localStorage.getItem('accountId') && !iss) {
        setError({ message: 'Account-ID or valid issuer is required.', translationKey: 'message.error.account_id_required' });
        setSearchParams(prev => { prev.delete('code'); prev.delete('session_state'); prev.delete('iss'); return prev; });
        setLoadingKeycloak(false);
      } else {
        keycloakLogin();
      }
      return;
    }

    // 3. Prevent auto-redirect after logout or Keycloak cancel (?skip_autologin=1)
    //    Strip param immediately so it doesn't persist in browser session restore
    if (searchParams.get('skip_autologin')) {
      sessionStorage.removeItem('skip_autologin');
      setSearchParams(prev => { prev.delete('skip_autologin'); return prev; });
      return;
    }

    // 4. Trigger Keycloak login — URL param accountId (existing) or auto-redirect when accountId is known
    if (accountId) {
      keycloakGetLogin();
    } else if (
      process.env.REACT_APP_ENV !== 'local' &&
      !sessionStorage.getItem('skip_autologin') &&
      localStorage.getItem('accountId')
    ) {
      keycloakGetLogin();
    }

    // 5. Handle bfcache (browser back-button from Keycloak)
    const handlePageShow = (e) => {
      if (e.persisted) setLoadingKeycloak(false);
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // --- Render ---

  return (
    <div className="container" data-testid="login">
      <div className="login-div">
        <div className="text-center">
          <Logo className="text-default-red logo" />
        </div>
        <div className="mt-20px">
          <div className="login-title">Bei Ihrem Konto anmelden</div>
          {
            error &&
            <ErrorAlert error={error} onClose={() => setError(null)} />
          }

          <form onSubmit={process.env.REACT_APP_ENV === 'local' ? handleSubmit(onSubmit) : e => e.preventDefault()} data-testid="login-form">
            <div className="form-group mb-0">
              <Field label="Account ID" htmlFor="account_id" required>
                <InputAffix readOnly={loadingKeycloak} suffix={
                  <button className="peb-input-affix__action" type={process.env.REACT_APP_ENV === 'local' ? 'button' : 'submit'} onClick={keycloakGetLogin} disabled={loading || loadingKeycloak} data-testid="continue-button">
                    {
                      loadingKeycloak ? <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Wird geladen...</span>
                      </div> : <i className="bi bi-arrow-right bi-bold text-smaller"></i>
                    }
                  </button>
                }>
                  <Input type="text" readOnly={loadingKeycloak} required id="account_id" data-testid="account-id-input" {...register('account_id', { onChange: onAccountIdChange })} />
                </InputAffix>
              </Field>
            </div>
            {
              process.env.REACT_APP_ENV === 'local' && <div className="mt-4">
                <div className="form-group">
                  <Field label="E-Mail-Adresse" htmlFor="email" required>
                    <Input type="email" required id="email" data-testid="email-input" {...register('email')} />
                  </Field>
                </div>
                <div className="form-group mb-4">
                  <Field label="Passwort" htmlFor="password" required>
                    <InputAffix suffix={
                      <button
                        type="button"
                        className="peb-input-affix__action"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="password-reveal"
                      ><i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'} text-smaller`}></i></button>
                    }>
                      <Input type={showPassword ? 'text' : 'password'} required id="password" data-testid="password-input" {...register('password')} />
                    </InputAffix>
                  </Field>
                </div>
                <div className="text-center">
                  <Button type="submit" variant="primary" className="btn-login w-100" loading={loading} title="Only valid for local environment" data-testid="login-button">Local Login [local env]</Button>
                </div>
              </div>
            }
          </form>
        </div >
      </div>
    </div >
  );
}
